import { ADMIN_BILLING_FILTERS, type AdminBillingFilter } from '@developerz/domain';
import { IconSearch } from '@developerz/ui';
import { batch, createEffect, createSignal, createUniqueId, type JSX, on, Show } from 'solid-js';
import { t } from '../i18n';
import { type AdminAccountRow, adminOrgHref, useAdminAccounts } from '../lib/admin';
import { usePagedQuery } from '../lib/paged';
import { AdminOrgDetailLoader } from './AdminOrgDetailLoader';
import { AdminOrgsTable } from './AdminOrgsTable';
import { EmptyState } from './EmptyState';
import { LoadMore } from './LoadMore';
import { PageHeader } from './PageHeader';
import { Panel } from './Panel';
import { QueryBoundary } from './QueryBoundary';
import { Select } from './Select';
import { Skeleton } from './Skeleton';
import { Button } from './ui/Button';

// Admin org registry container. Cursor-paginated (`GET /v1/admin/accounts`) with
// a `q` search box; selecting a row mounts its detail loader (detail + members).
// Mounts only for staff (behind <AdminGate/>). Paging lives in `usePagedQuery`;
// this owns the search box. The billing filter and the open org are the
// route's (the URL's): read through `billing` / `selected`; the filter changes
// through `onBilling`, the open org through the row's link (`adminOrgHref`,
// which keeps the filter). Before a pick, the detail column says what goes there.

/** The select's value: `all` is the ABSENCE of the filter, never sent. */
type BillingChoice = AdminBillingFilter | 'all';

/** Exhaustive by type: a new `ADMIN_BILLING_FILTERS` member without a label fails to compile. */
const billingLabel = (f: AdminBillingFilter): string =>
  ({
    paying: t('admin.billing_paying'),
    comped: t('admin.billing_comped'),
    paused: t('admin.billing_paused'),
  })[f];

export function AdminOrgsPanel(props: {
  token: string | null;
  billing: () => AdminBillingFilter | null;
  onBilling: (next: AdminBillingFilter | null) => void;
  selected: () => string | null;
}): JSX.Element {
  const billingId = createUniqueId();
  const [draft, setDraft] = createSignal('');
  const [q, setQ] = createSignal('');

  const list = usePagedQuery<AdminAccountRow>((cursor) =>
    useAdminAccounts(props.billing, q, cursor, 30, props.token),
  );

  // The filter arrives from the URL, so it can move without this panel's say —
  // the select, the overview's links, the rail's bare `/admin/orgs`. Whichever,
  // the walk restarts at page one. ponytail: an effect, not a batch with the
  // write, because the router applies the URL in a transition; the ceiling is one
  // extra page read when the filter moves after a Load more.
  createEffect(on(props.billing, () => list.reset(), { defer: true }));

  // The new search and the reset are ONE batched write, so the query key jumps
  // straight to `cursor=null` rather than firing a throwaway fetch at the cursor
  // the previous search had reached. A re-search for the SAME text stays a
  // no-op: with no key change to refill them, resetting would just empty the
  // rows already on screen.
  const search = (): void => {
    const next = draft().trim();
    if (next === q()) return;
    batch(() => {
      setQ(next);
      list.reset();
    });
  };

  return (
    <>
      <PageHeader title={t('admin.orgs_title')} desc={t('admin.orgs_subtitle')} />

      {/* A semantic <search>, NOT a <form>: an async-resolving detail sibling +
          the list <For/> trips a happy-dom template-walk crash when a real
          <form> is a static sibling. Search fires on the button or Enter. */}
      <search class="filter-row">
        <div class="admin-filter__select">
          <label id={`${billingId}-label`} for={billingId} class="sr-only">
            {t('admin.orgs_billing_label')}
          </label>
          <Select<BillingChoice>
            a11y={{ id: billingId }}
            label={t('admin.orgs_billing_label')}
            value={props.billing() ?? 'all'}
            options={[
              { value: 'all', label: t('admin.billing_all') },
              ...ADMIN_BILLING_FILTERS.map((f) => ({ value: f, label: billingLabel(f) })),
            ]}
            onChange={(next) => props.onBilling(next === 'all' ? null : next)}
          />
        </div>
        <div class="search-input">
          <IconSearch size="1rem" />
          <input
            id="admin-orgs-q"
            type="search"
            class="input"
            placeholder={t('admin.orgs_search_label')}
            aria-label={t('admin.orgs_search_label')}
            value={draft()}
            onInput={(e) => setDraft(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') search();
            }}
          />
        </div>
        <Button variant="outline" onClick={search}>
          {t('admin.search')}
        </Button>
      </search>

      <div class="admin-orgs__layout">
        <Panel title={t('admin.orgs_all')}>
          <QueryBoundary
            query={list.view}
            isEmpty={(r) => r.length === 0}
            loading={
              <div class="admin__skeletons">
                <Skeleton height="3rem" />
                <Skeleton height="3rem" />
              </div>
            }
            empty={<EmptyState message={t('admin.orgs_empty')} />}
          >
            {(r) => (
              <>
                <AdminOrgsTable
                  rows={r}
                  selectedId={props.selected()}
                  orgHref={(id) => adminOrgHref(id, props.billing())}
                />
                <LoadMore list={list} label={t('admin.load_more')} />
              </>
            )}
          </QueryBoundary>
        </Panel>

        {/* `keyed` recreates the detail per selection — and, critically, sidesteps
            a happy-dom reconciliation crash when an unkeyed <Show/> toggles as a
            sibling of the list's <For/> (markers desync). */}
        <Show
          when={props.selected()}
          keyed
          fallback={
            <Panel title={t('admin.orgs_detail_title')}>
              <EmptyState message={t('admin.orgs_detail_empty')} />
            </Panel>
          }
        >
          {(id) => (
            <AdminOrgDetailLoader id={id} token={props.token} onMutated={() => list.reload()} />
          )}
        </Show>
      </div>
    </>
  );
}
